<script setup lang="ts">
import {deepClone, type FileReference, type News, NewsType, TagNamespace, translate} from "~/composables/charon";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";

const { moment } = useMoment()
const { charon } = useCharon()
const { d, t, locales, locale } = useI18n()
const provider = ref(null)

const props = defineProps<{
  content: News
  preview?: boolean
}>()

const emit = defineEmits<{
  (e: "update:content", value: News): void
}>()

const selectableLocales = computed(() => Object.keys(props.content.locales))
const selectedLocale = ref(
  // try select current locale
  selectableLocales.value.find(l => l == locale.value)
  // try select any language
  ?? selectableLocales.value.find(_ => true)
  ?? null
)

const fileViewerOpen = ref(false)

const content = computed({
  get: () => props.content,
  set: (val) => emit('update:content', val)
})

const formatPublishDate = computed({
  get: () => moment.utc(content.value.publishDate).local().format(moment.HTML5_FMT.DATETIME_LOCAL),
  set: (val) => content.value.publishDate = moment(val, moment.HTML5_FMT.DATETIME_LOCAL).utc().toISOString()
})

function addLocale(locale: string) {
  content.value.locales = {
    ...content.value.locales,
    // use current editor content or empty
    [locale]: deepClone(content.value.locales[selectedLocale.value]) ?? {
      image: null,
      title: null,
      teaser: null,
      body: null,
    }
  }

  selectedLocale.value = locale
}

function removeLocale(locale: string) {
  const name = locales.value.find(l => l.code == locale)?.name ?? '-'
  if (!window.confirm(t('jc.component.newsEditor.confirmRemoveLocale.label', { locale: name }))) {
    return
  }

  // remove from body
  content.value.locales = Object.fromEntries(
    Object.entries(content.value.locales)
      .filter(([l, _]) => l !== locale)
  )

  if (selectedLocale.value == locale) {
    selectedLocale.value = selectableLocales.value.length > 0 ? selectableLocales.value[0] : null
  }
}

const image = computed(() => translate(props.content.locales, selectedLocale.value)?.image)
const { data: reference } = useAsyncData(
  `file-reference-${image.value ?? 'placeholder'}`,
  async () => {
    if (!image.value) {
      return null
    }
    return charon<FileReference>(`/file-references/${image.value}`)
  },
  {
    lazy: true,
    server: true,
    watch: [ image ]
  }
)

function selectImage(images: string[]) {
  content.value.locales[selectedLocale.value!].image = images[0]
  fileViewerOpen.value = false
}
</script>

<template>
  <div class="flex flex-col gap-6 text-gray-900">
    <client-only>
      <div
        v-if="!props.preview"
        class="rounded-md shadow-lg border border-gray-200 bg-white flex flex-col"
      >
        <div class="flex flex-col gap-8 m-card text-gray-900">
          <!-- news: type -->
          <div class="flex flex-col gap-1">
            <label
              class="flex flex-col gap-2"
              :title="t('jc.component.newsEditor.typeInput.title')"
            >
            <span class="b2">
              {{ t('jc.component.newsEditor.typeInput.label') }}
            </span>
              <select
                class="h-full px-3 py-1"
                v-model="content.type"
              >
                <template v-for="type in Object.values(NewsType)">
                  <option :value="type">
                    {{ t(`jc.news.type.${type}.title`) }}
                  </option>
                </template>
              </select>
            </label>
            <span v-if="!content.type" class="b2 text-red-500">
              {{ t('jc.component.newsEditor.typeInput.error') }}
            </span>
          </div>

          <!-- news: publish settings -->
          <div class="flex flex-col gap-2">
            <label
              class="flex flex-col gap-2"
              :title="t('jc.component.newsEditor.publishInput.date.title')"
            >
              <span class="b2">
                {{ t('jc.component.newsEditor.publishInput.date.label') }}
              </span>
              <input
                v-model="formatPublishDate"
                type="datetime-local"
              >
            </label>

            <label
              class="flex flex-row gap-2"
              :title="t('jc.component.newsEditor.publishInput.draft.title')"
            >
              <input
                class=""
                type="checkbox"
                v-model="content.draft"
                name="news-status"
              >
              <span class="b2">
                {{ t('jc.component.newsEditor.publishInput.draft.label') }}
              </span>
            </label>

            <span v-if="!content.draft && !content.publishDate" class="b2 text-red-500">
              {{ t('jc.component.newsEditor.publishInput.error') }}
            </span>
          </div>

          <!-- news: slug -->
          <div class="flex flex-col gap-1">
            <label
              class="flex flex-col gap-2"
              :title="t('jc.component.newsEditor.slugInput.title')"
            >
              <span class="b2">
                {{ t('jc.component.newsEditor.slugInput.label') }}
              </span>
              <input
                v-model="content.slug"
                type="text"
                :placeholder="t('jc.component.newsEditor.slugInput.placeholder')"
              >
            </label>
            <span v-if="!content.slug" class="b2 text-red-500">
              {{ t('jc.component.newsEditor.slugInput.error') }}
            </span>
          </div>

          <!-- news tags -->
          <div class="flex flex-col gap-1">
            <label
              class="flex flex-col gap-2"
              for="combobox-input"
              :title="t('jc.component.newsEditor.tagInput.title')"
            >
              <span class="b2">
                {{ t('jc.component.newsEditor.tagInput.label') }}
              </span>
              <TagsInput
                v-model:tag-ids="content.tags"
                :namespace="TagNamespace.NEWS"
                :expected-locales="selectableLocales"
              />
            </label>
            <span v-if="!content.tags.length" class="b2 text-red-500">
              {{ t('jc.component.newsEditor.tagInput.error') }}
            </span>
          </div>
        </div>
      </div>
    </client-only>

    <client-only>
      <div
        v-if="!props.preview"
        class="flex flex-col gap-2 mt-8"
      >
        <span class="b2">{{ t('jc.component.newsEditor.locales.label') }}</span>
        <LocaleSelector
          :locales="locales"
          :selectable="selectableLocales"
          v-model:selected="selectedLocale"
          @click:add="addLocale"
          @click:remove="removeLocale"
        />
        <span
          v-if="selectableLocales.length == 0"
          class="b2 text-red-500"
        >
          {{ t('jc.component.newsEditor.locales.error') }}
        </span>
      </div>
    </client-only>

    <template v-if="selectedLocale">
      <div
        v-if="!props.preview"
        class="rounded-md shadow-lg border border-gray-200 bg-white flex flex-col"
        :title="t('jc.component.newsEditor.teaserInput.title')"
      >
        <div class="flex flex-col gap-4 m-card text-gray-900">
          <span class="b2">
            {{ t('jc.component.newsEditor.teaserInput.label') }}
          </span>
          <NewsEditorTeaser
            :provider="provider"
            :field="`news-teaser-${selectedLocale}`"
            :editable="!props.preview"
            v-model:content="content.locales[selectedLocale].teaser"
            :key="selectedLocale"
          />
        </div>
      </div>

      <div class="rounded-md shadow-lg border border-gray-200 bg-white flex flex-col">
        <div class="relative flex flex-col group/image bg-white rounded-t-md">
          <NuxtPicture
            v-if="reference"
            :src="`/charon/${reference.id}`"
            :alt="translate(reference.locales, selectedLocale)?.description"
            loading="eager"
            sizes="640px laptop:960px desktop:1078px"
            class="rounded-t-md w-full overflow-hidden bg-inherit"
            height="24rem"
            :img-attrs="{
              class: 'object-cover object-center w-full h-96'
            }"
          />
          <div v-else class="w-full h-96 bg-gray-200 animate-pulse rounded-t-md" />
          <button
            v-if="!props.preview"
            class="absolute top-4 right-4 bg-primary hover:bg-primary-highlighted active:bg-primary-selected disabled:text-opacity-50 text-white rounded-full p-2 hidden group-hover/image:block aspect-square"
            @click="fileViewerOpen = true"
          >
            <FontAwesomeIcon
              icon="pen"
              size="lg"
              fixed-width
            />
          </button>
          <client-only>
            <FileViewer
              v-if="fileViewerOpen"
              select="single"
              :filter="{
                'mediaType[fuzzyi]': 'image/'
              }"
              @click:select="selectImage"
              @click:close="() => fileViewerOpen = false"
            />
          </client-only>
        </div>

        <span
          class="bg-primary h-full w-full
            min-h-[10px] min-w-[10px] laptop:min-h-[15px] laptop:min-w-[15px]"
        />

        <div class="flex flex-col gap-4 m-card text-gray-900">
          <NewsEditorTitle
            id="news-editor-title"
            :provider="provider"
            :field="`news-title-${selectedLocale}`"
            v-model:content="content.locales[selectedLocale].title"
            :editable="!props.preview"
            class="text-slate-900"
            :key="selectedLocale"
          />

          <div class="flex flex-row gap-2 mt-auto">
            <span class="b2 text-slate-700">
              {{ t(`jc.news.type.${content.type}.title`) }}
            </span>
            <span v-if="content.publishDate" class="b2 text-slate-700">
              {{ d(content.publishDate ?? '', 'short') }}
            </span>
          </div>

          <NewsEditorBody
            id="news-editor-body"
            :provider="provider"
            :field="`news-body-${selectedLocale}`"
            v-model:content="content.locales[selectedLocale].body"
            :editable="!props.preview"
            :key="selectedLocale"
          />
        </div>
      </div>
    </template>
  </div>
</template>
